import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * WebAuthn JSON shapes exchanged with `@simplewebauthn/browser`. Every field
 * the server emits or the verifier reads is listed; anything else is stripped.
 * (Loose objects would add an index signature that the library's interface
 * types can't satisfy.)
 */
const Base64UrlSchema = z.string().min(1);

// `type`/`transports` are plain strings to match the library's descriptor type;
// browsers ignore values they don't recognise.
const CredentialDescriptorSchema = z.object({
  id: Base64UrlSchema,
  type: z.string(),
  transports: z.array(z.string()).optional(),
});

const AuthenticatorSelectionSchema = z.object({
  authenticatorAttachment: z.enum(["cross-platform", "platform"]).optional(),
  requireResidentKey: z.boolean().optional(),
  residentKey: z.enum(["discouraged", "preferred", "required"]).optional(),
  userVerification: z.enum(["discouraged", "preferred", "required"]).optional(),
});

const RegistrationOptionsSchema = z.object({
  rp: z.object({ id: z.string().optional(), name: z.string() }),
  user: z.object({
    id: Base64UrlSchema,
    name: z.string(),
    displayName: z.string(),
  }),
  challenge: Base64UrlSchema,
  pubKeyCredParams: z.array(
    z.object({ alg: z.number(), type: z.literal("public-key") }),
  ),
  timeout: z.number().optional(),
  excludeCredentials: z.array(CredentialDescriptorSchema).optional(),
  authenticatorSelection: AuthenticatorSelectionSchema.optional(),
  attestation: z.enum(["direct", "enterprise", "indirect", "none"]).optional(),
  extensions: z.object({ credProps: z.boolean().optional() }).optional(),
});

const AuthenticationOptionsSchema = z.object({
  challenge: Base64UrlSchema,
  timeout: z.number().optional(),
  rpId: z.string().optional(),
  allowCredentials: z.array(CredentialDescriptorSchema).optional(),
  userVerification: z.enum(["discouraged", "preferred", "required"]).optional(),
});

const CredentialResponseBaseSchema = {
  id: Base64UrlSchema,
  rawId: Base64UrlSchema,
  authenticatorAttachment: z.enum(["cross-platform", "platform"]).optional(),
  clientExtensionResults: z.object({
    credProps: z.object({ rk: z.boolean().optional() }).optional(),
  }),
  type: z.literal("public-key"),
};

const RegistrationResponseSchema = z.object({
  ...CredentialResponseBaseSchema,
  response: z.object({
    clientDataJSON: Base64UrlSchema,
    attestationObject: Base64UrlSchema,
    authenticatorData: Base64UrlSchema.optional(),
    transports: z.array(z.string()).optional(),
    publicKeyAlgorithm: z.number().optional(),
    publicKey: Base64UrlSchema.optional(),
  }),
});

const AuthenticationResponseSchema = z.object({
  ...CredentialResponseBaseSchema,
  response: z.object({
    clientDataJSON: Base64UrlSchema,
    authenticatorData: Base64UrlSchema,
    signature: Base64UrlSchema,
    userHandle: Base64UrlSchema.optional(),
  }),
});

const PasskeyNameSchema = z.string().trim().min(1).max(100);

const PasskeySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  deviceType: z.enum(["singleDevice", "multiDevice"]),
  backedUp: z.boolean(),
  createdAt: z.coerce.date(),
  lastUsedAt: z.coerce.date().nullable(),
});

const ListPasskeysResponseSchema = z.object({
  passkeys: z.array(PasskeySchema),
  // False for SSO-only users who haven't been granted `create:Passkey`.
  canRegister: z.boolean(),
});

const PasskeyLoginInputSchema = z.object({
  response: AuthenticationResponseSchema,
});

const RegisterPasskeyInputSchema = z.object({
  response: RegistrationResponseSchema,
  name: PasskeyNameSchema.optional(),
});

const RenamePasskeyInputSchema = z.object({
  id: z.string().uuid(),
  name: PasskeyNameSchema,
});

const DeletePasskeyInputSchema = z.object({
  id: z.string().uuid(),
});

const OkResponseSchema = z.object({ ok: z.literal(true) });

/**
 * Passkey (WebAuthn) login. Both procedures are public — see PUBLIC_RPC_PATHS
 * in `backend/src/api/routes/rpc/express.adapter.ts`.
 */
export const passkeyLoginContracts = {
  // Issue a single-use challenge (stored on the session) for a discoverable
  // credential login. POST because it writes the challenge to the session.
  passkeyLoginOptions: oc.output(AuthenticationOptionsSchema).route({
    method: "POST",
    summary: "Start passkey sign-in",
    description:
      "Issues a single-use sign-in challenge stored on the session and returns the browser options needed to sign in with a passkey.",
  }),
  // Verify the signed assertion against the stored challenge and log in.
  passkeyLogin: oc
    .input(PasskeyLoginInputSchema)
    .output(OkResponseSchema)
    .route({
      method: "POST",
      summary: "Sign in with a passkey",
      description:
        "Verifies the signed passkey response against the issued challenge and starts the user's session.",
    }),
} as const;

/** Manage the signed-in user's own passkeys. Session-authenticated. */
export const passkeyContracts = {
  registrationOptions: oc.output(RegistrationOptionsSchema).route({
    method: "POST",
    summary: "Start passkey registration",
    description:
      "Issues a registration challenge for the signed-in user and returns the browser options to create a new passkey. Not allowed while imitating a user.",
  }),
  register: oc.input(RegisterPasskeyInputSchema).output(PasskeySchema).route({
    method: "POST",
    summary: "Register a new passkey",
    description:
      "Verifies the browser's registration response and saves a new passkey, optionally named, for the signed-in user. Returns the saved passkey.",
  }),
  list: oc.output(ListPasskeysResponseSchema).route({
    method: "GET",
    summary: "List my passkeys",
    description:
      "Returns the signed-in user's passkeys with device type, backup state, created and last-used dates, plus whether they may register new ones.",
  }),
  rename: oc.input(RenamePasskeyInputSchema).output(PasskeySchema).route({
    method: "POST",
    summary: "Rename a passkey",
    description:
      "Changes the display name of one of the signed-in user's passkeys and returns the updated passkey.",
  }),
  delete: oc.input(DeletePasskeyInputSchema).output(OkResponseSchema).route({
    method: "POST",
    summary: "Delete a passkey",
    description:
      "Removes one of the signed-in user's passkeys so it can no longer be used to sign in.",
  }),
} as const;

export type Passkey = z.infer<typeof PasskeySchema>;
export type PasskeyRegistrationOptions = z.infer<
  typeof RegistrationOptionsSchema
>;
export type PasskeyAuthenticationOptions = z.infer<
  typeof AuthenticationOptionsSchema
>;
export type PasskeyRegistrationResponse = z.infer<
  typeof RegistrationResponseSchema
>;
export type PasskeyAuthenticationResponse = z.infer<
  typeof AuthenticationResponseSchema
>;
